import { TpDataTable } from '../../../../data/interfaces';
import { convertColumnDataToDisplayString } from '../../helpers/misc';

/**
 * Checks whether the entity (itemId) in the given dataTable meets the requirements
 * specified by requiredProperty and requiredPropertyValues.
 *
 * @param dataTable The data table containing the entity
 * @param itemId The ID of the entity to check
 * @param requiredProperty The property (column ID) that must be present
 * @param requiredPropertyValues Optional list of acceptable values for the required property
 * @returns True if the entity meets the requirements, false otherwise
 */
export function entityIsCompatible(
  dataTable: TpDataTable,
  itemId: string,
  requiredProperty: string | null | undefined,
  requiredPropertyValues: any[] | undefined,
): boolean {
  if (!requiredProperty) {
    return true; // No required property specified
  }
  let isCompatible = true;

  if (itemId in dataTable.itemIdMap) {
    const itemIdx = dataTable.itemIdMap[itemId];
    const itemIdValue = dataTable.columnData[requiredProperty][itemIdx];
    if (requiredPropertyValues !== undefined) {
      if (Array.isArray(itemIdValue)) {
        isCompatible = itemIdValue.some((val) => requiredPropertyValues.includes(val));
      } else {
        isCompatible = requiredPropertyValues.includes(itemIdValue);
      }
    } else if (Array.isArray(itemIdValue)) {
      isCompatible = itemIdValue.length > 0;
    } else {
      isCompatible = itemIdValue !== '';
    }
  }
  return isCompatible; // Returns true if item not in table (default) or if compatibility check passes
}

/**
 * Formats a template string by replacing placeholder column IDs with actual values
 * for this entity.
 * @param stringTemplate string template with placeholders like {columnId}
 * @param dataTable The data table containing the entity
 * @param itemId The ID of the entity to render
 * @returns The formatted string with placeholders replaced by actual values
 */
export function formatDataTableStringTemplate(stringTemplate: string, dataTable: TpDataTable, itemId: string) {
  if (itemId in dataTable.itemIdMap) {
    const itemIdx = dataTable.itemIdMap[itemId];
    for (const col of dataTable.columns) {
      stringTemplate = stringTemplate.replaceAll(
        '{' + col.id + '}',
        convertColumnDataToDisplayString(col, dataTable.columnData[col.id][itemIdx]),
      );
    }
  }
  return stringTemplate;
}
